// packages/storefront/src/translation/domGuardImpl.ts
//
// All behaviour of the embed's DOM guard (docs/embed-browser-translation.md; the frozen install surface and the
// contract C1-C6 are in domGuard.ts). ANY change to this file bumps DOM_GUARD_IMPL_VERSION and records its hash in
// domGuard.install.test.ts (rollback is roll-forward).
//
// Model: a GROUP is a run of a parent's children that a page translator rewrote. members = React's nodes of that run
// in React's order; each text member may have its own DISPLAY (itself, a translator node that replaced it 1:1, or
// our clone). A rebuild restores React's view with minimal change: elements are always the originals; an unchanged
// text keeps its display (its translation survives); a text React changed, or any text of a group that cannot be
// split 1:1 (a merged run), is shown by a FRESH clone (so every engine translates it again). Nothing the guard cannot
// attribute is ever removed.
//
// Ownership is by the registered react-dom copies' `__reactFiber$<k>` keys; observation follows ownership (our roots
// and our portal nodes, never the partner's page). A copy registered for a SUBTREE (impl v3: a TourTask site, where
// Next hydrates the whole document and the storefront is one part of it) owns the page around the subtree too; there
// observation stays on the subtree and on the portal containers React did not render.

import { EXACT_VALUE_ATTR } from '@tourtask/ui/components/exactValueAttr';

import type { DomGuardOptions, DomGuardRegistryV1, DomGuardWindow } from './domGuard';
import { initDomGuardState } from './domGuardState';
import type { DomGuardEvent, DomGuardEventKind, DomGuardFault, DomGuardGroup, DomGuardStats } from './domGuardState';

export type { DomGuardEvent, DomGuardEventKind, DomGuardStats } from './domGuardState';

export type DomGuardStatsSnapshot = DomGuardStats & {
	off: boolean;
	active: boolean;
	keys: number;
	implVersion: number;
	diag: DomGuardRegistryV1['diag'];
	/** The first internal faults (where + message), for the harness and the device matrix. */
	faultsList: DomGuardFault[];
};

export interface DomGuardImpl {
	removeChild(parent: unknown, child: unknown, args: IArguments): unknown;
	insertBefore(parent: unknown, node: unknown, ref: unknown, args: IArguments): unknown;
	appendChild(parent: unknown, node: unknown, args: IArguments): unknown;
	setNodeValue(node: unknown, value: unknown, args: IArguments): void;
	process(records: MutationRecord[]): void;
	checkInstall(): void;
	/** Call right after createRoot(container), before render. false => this react-dom copy's key was not found (reported). */
	registerReactRoot(container: Element): boolean;
	/**
	 * Impl v3. For a widget that is one SUBTREE of a larger React root it does not own (a framework hydrating the
	 * document): the react-dom copy's key is read from `rootContainer` (the node `hydrateRoot` was given, e.g.
	 * `document`), and only `subtree` is observed, with its portals. Call once the subtree is in the page. false => no
	 * key on `rootContainer` (reported as `no-react-key`).
	 */
	registerReactSubtree(rootContainer: Node, subtree: Element): boolean;
	/** Page-level events; delivered to the FIRST listener that does not throw, once per kind per page view. */
	onEvent(listener: (e: DomGuardEvent) => void): void;
	stats(): DomGuardStatsSnapshot;
}

const TEXT = 3;
const ELEMENT = 1;
const OBSERVE: MutationObserverInit = { childList: true, subtree: true, characterData: true, characterDataOldValue: true };
const MAX_FAULTS = 20;
const REPAIR_WINDOW_MS = 10_000;
const MAX_REPAIRS = 3;
const MAX_ISLAND_RESTORES = 3;
/** Node.DOCUMENT_POSITION_FOLLOWING */
const FOLLOWING = 4;

/**
 * What a trampoline does after its decision: the native call (PASS), nothing more (DONE), or the native call plus
 * bookkeeping applied only once it returned: React's own removal (TAG), an accepted-looking insert (NOTE).
 */
type Plan = 'pass' | 'done' | 'tag' | 'note';
const PASS: Plan = 'pass';
const DONE: Plan = 'done';
const TAG: Plan = 'tag';
const NOTE: Plan = 'note';

const isText = (n: Node): n is Text => n.nodeType === TEXT;
const isElement = (n: Node): n is Element => n.nodeType === ELEMENT;
/** Text, CDATA, processing instruction, comment: the targets of characterData records. */
const isCharacterData = (n: Node): n is CharacterData => n.nodeType === 3 || n.nodeType === 4 || n.nodeType === 7 || n.nodeType === 8;

function faultMessage(e: unknown): string {
	const m = typeof e === 'object' && e !== null && 'message' in e ? e.message : undefined;
	return String(m || e);
}

function isConstructorOf(c: unknown, proto: object): c is typeof Node {
	return typeof c === 'function' && Reflect.get(c, 'prototype') === proto;
}

/**
 * The page's real `Node` constructor, found through the document's prototype chain, never through the global `Node`
 * binding: that one is writable, and a classic page script with a top-level `function Node(){}` or `var Node = …`
 * replaces it, before or after our bundles load. Every receiver and ownership check would then answer "not a node"
 * and pass React's failing call straight to the native method (the 2026-09-22 crash). `window.document` is
 * unforgeable and its chain always runs through Node.prototype, the one prototype that owns `removeChild` and
 * `nodeValue`; a built-in constructor's `prototype` is non-writable, so `x instanceof` it stays exact. null: not a DOM
 * the guard understands (the widget then renders unguarded).
 */
export function nodeConstructorOf(doc: Document): typeof Node | null {
	const own = (o: object, k: string) => Object.prototype.hasOwnProperty.call(o, k);
	for (let p: unknown = Object.getPrototypeOf(doc); typeof p === 'object' && p !== null; p = Object.getPrototypeOf(p)) {
		if (own(p, 'removeChild') && own(p, 'nodeValue')) {
			const ctor: unknown = Reflect.get(p, 'constructor');
			return isConstructorOf(ctor, p) ? ctor : null;
		}
	}
	return null;
}

export function createDomGuardImpl(reg: DomGuardRegistryV1, win: DomGuardWindow, opts: Pick<DomGuardOptions, 'log'>): DomGuardImpl {
	const S = initDomGuardState(reg.state);
	const B = reg.below;
	const doc = win.document;
	// Captured once; see nodeConstructorOf. The registry only exists once its install found Node.prototype through the
	// same document, so this cannot be null on a registry an impl is created for.
	const NodeCtor = nodeConstructorOf(doc);
	if (NodeCtor === null) throw new Error('createDomGuardImpl: the document has no Node.prototype in its chain');
	const NP = NodeCtor.prototype;
	const isNode = (x: unknown): x is Node => x instanceof NodeCtor;
	const log = opts.log || (() => {});
	const qm: (f: () => void) => void =
		typeof win.queueMicrotask === 'function'
			? win.queueMicrotask.bind(win)
			: (f) => {
					void Promise.resolve().then(f);
				};
	const now = () => Date.now();
	/** The data of a text/comment node through the getter below us: never null for CharacterData (DOM Standard, Node.nodeValue). */
	const nvGet = (n: CharacterData): string => {
		const v = B.nodeValueGet.call(n);
		if (typeof v !== 'string') throw new TypeError(`nodeValue of a ${n.nodeName} node is not a string`);
		return v;
	};

	// ------------------------------------------------------------ ownership
	function reactOwned(n: unknown): boolean {
		if (n == null || typeof n !== 'object') return false;
		const ks = S.keys;
		for (let i = 0; i < ks.length; i++) if (Reflect.get(n, ks[i]) !== undefined) return true;
		return false;
	}
	/** Our clone standing in for a React text. */
	function memberClone(n: Node): boolean {
		const o = S.origin.get(n);
		return o !== undefined && reactOwned(o);
	}
	const memberLike = (n: Node) => reactOwned(n) || memberClone(n);
	// writtenInPlace covers React's lone text children (created by textContent=, so they carry no fiber)
	const tracked = (n: Node) => reactOwned(n) || S.origin.has(n) || S.writtenInPlace.has(n);
	const isBlank = (n: Node) => isText(n) && !/\S/.test(nvGet(n));
	const squash = (s: string) => s.replace(/\s+/g, ' ').trim();
	// identical words, different numbers: e.g. "Only 5 seats left" vs React's "Only 7 seats left"
	const sameWordsOtherNumbers = (a: string, b: string) => {
		const x = squash(a);
		const y = squash(b);
		return x !== y && x.replace(/\d+/g, '#') === y.replace(/\d+/g, '#');
	};
	const reactText = (m: CharacterData): string => {
		const written = S.reactValue.get(m);
		if (written !== undefined) return written;
		const before = S.truth.get(m);
		if (before !== undefined) return before;
		return nvGet(m);
	};

	function fault(where: string, e: unknown): void {
		S.stats.faults++;
		const message = faultMessage(e);
		if (S.lastFaults.length < 5) S.lastFaults.push({ where, message });
		log({ op: 'FAULT ' + where, message });
		if (S.stats.faults >= MAX_FAULTS && !S.off) {
			S.off = true;
			emit('breaker');
		}
	}
	function emit(kind: DomGuardEventKind, detail?: Record<string, string>): void {
		if (S.emitted.has(kind)) return;
		S.emitted.add(kind);
		for (const l of S.listeners) {
			try {
				const e: DomGuardEvent = { kind, implVersion: reg.implVersion, stats: { ...S.stats } };
				if (detail !== undefined) e.detail = detail;
				l(e);
				return;
			} catch {
				/* a broken listener must not break the guard; try the next */
			}
		}
	}

	// ------------------------------------------------------------ scheduling
	function schedule(): void {
		if (!S.scheduled) {
			S.scheduled = true;
			qm(settle);
		}
	}
	const scheduleGroup = (g: DomGuardGroup) => {
		S.pendingGroups.add(g);
		schedule();
	};
	function tagViaCall(n: Node): void {
		S.viaCall.add(n);
		if (!S.viaCallClearQueued) {
			S.viaCallClearQueued = true;
			// queued AFTER the native call, so the observer delivery (queued by it) runs first
			qm(() => {
				S.viaCallClearQueued = false;
				S.viaCall.clear();
			});
		}
	}
	function noteCallInsert(node: Node, ref: Node): void {
		S.callInsBefore.set(node, ref);
		if (!S.callInsClearQueued) {
			S.callInsClearQueued = true;
			qm(() => {
				S.callInsClearQueued = false;
				S.callInsBefore.clear();
			});
		}
	}

	// ------------------------------------------------------------ observation follows ownership
	function observe(t: Node): void {
		reg.mo.observe(t, OBSERVE);
		S.covered.add(t);
		S.stats.autoObserved++;
	}
	function ensureCovered(P: Node, node: Node): void {
		if (S.keys.length === 0 || !P.isConnected || !reactOwned(node)) return; // render-phase appends build DETACHED trees
		// The partner's page containers: observe OUR node, never the container (G7). <body> takes MUI's portal roots;
		// <head> takes React 19's hoistables (a <title>, <meta> or <link> rendered anywhere in our tree), and observing
		// <head> itself would deliver every partner script and style mutation there to our observer.
		if (P === doc.body || P === doc.head || P === doc.documentElement || P === doc) {
			if (node.nodeType === ELEMENT && !S.covered.has(node)) observe(node);
			return;
		}
		for (let a: Node | null = P; a; a = a.parentNode) if (S.covered.has(a)) return;
		// A copy registered for a subtree renders the page around it too: a parent React rendered there is the page's
		// own, never observed (G7). A parent React did not render is a portal container, and is.
		if (S.subtreeKeys.length > 0 && reactOwned(P) && S.subtreeKeys.includes(ownerKey(node))) return;
		observe(P); // any other portal container (cart bar, fly-to-cart host, ...)
	}
	/** The registered key `n` carries (a React node is created by exactly one react-dom copy), or '' for none. */
	function ownerKey(n: object): string {
		const ks = S.keys;
		for (let i = 0; i < ks.length; i++) if (Reflect.get(n, ks[i]) !== undefined) return ks[i];
		return '';
	}
	/**
	 * Takes the `__reactFiber$<k>` key of every react-dom copy that created a root on `container` (it marks the
	 * container with `__reactContainer$<k>`). A copy registered for a subtree only is remembered as such; a root of its
	 * own makes it a whole-root copy again.
	 */
	function registerKeysOf(container: object, subtreeOnly: boolean): boolean {
		let found = false;
		for (const k of Object.keys(container)) {
			if (k.startsWith('__reactContainer$')) {
				const fk = '__reactFiber$' + k.slice('__reactContainer$'.length);
				if (!S.keys.includes(fk)) {
					S.keys.push(fk);
					if (subtreeOnly) S.subtreeKeys.push(fk);
				} else if (!subtreeOnly) {
					const i = S.subtreeKeys.indexOf(fk);
					if (i !== -1) S.subtreeKeys.splice(i, 1);
				}
				found = true;
			}
		}
		return found;
	}

	// ------------------------------------------------------------ groups
	function newGroup(parent: Node, members: Node[], nodes: Iterable<Node>, prevAnchor: Node | null, nextAnchor: Node | null): DomGuardGroup {
		const g: DomGuardGroup = { id: ++S.seq, parent, members, nodes: new Set(nodes), prevAnchor, nextAnchor, changed: new Set(), forceClone: new Set(), dirty: false, dead: false };
		for (const m of members) {
			const old = S.memberOf.get(m);
			if (old && old !== g) detachMember(old, m);
			S.memberOf.set(m, g);
		}
		for (const n of g.nodes) S.regionOf.set(n, g);
		S.stats.groups++;
		S.active = true;
		return g;
	}
	function dissolve(g: DomGuardGroup): void {
		g.dead = true;
		for (const m of g.members) if (S.memberOf.get(m) === g) S.memberOf.delete(m);
		for (const n of g.nodes) if (S.regionOf.get(n) === g) S.regionOf.delete(n);
	}
	function detachMember(g: DomGuardGroup, m: Node): void {
		const i = g.members.indexOf(m);
		if (i >= 0) g.members.splice(i, 1);
		if (S.memberOf.get(m) === g) S.memberOf.delete(m);
		g.changed.delete(m);
		// its display (a translator node / our clone) stays in the region, so the rebuild removes it
		const d = displayAnywhere(m);
		if (d && d !== m) {
			g.nodes.add(d);
			S.regionOf.set(d, g);
		}
		g.dirty = true;
	}
	function insertMember(g: DomGuardGroup, node: Node, ref: Node | null): void {
		const old = S.memberOf.get(node);
		if (old) {
			detachMember(old, node);
			if (old !== g) scheduleGroup(old);
		}
		const i = ref == null ? -1 : g.members.indexOf(ref);
		g.members.splice(i < 0 ? g.members.length : i, 0, node);
		S.memberOf.set(node, g);
		g.dirty = true;
	}
	/** What shows text member m inside P right now (itself, a 1:1 stand-in, our clone, the clone's stand-in). */
	function display(m: Node, P: Node): Node | null {
		let n: Node | undefined = m;
		for (let i = 0; i < 4 && n; i++) {
			if (n.parentNode === P) return n;
			const s = S.standIn.get(n);
			if (s !== undefined && s.parentNode === P) return s;
			n = S.cloneOf.get(n);
		}
		return null;
	}
	function displayAnywhere(m: Node): Node | null {
		let n: Node | undefined = m;
		for (let i = 0; i < 4 && n; i++) {
			if (n !== m && n.parentNode) return n;
			const s = S.standIn.get(n);
			if (s !== undefined && s.parentNode) return s;
			n = S.cloneOf.get(n);
		}
		return m.parentNode ? m : null;
	}
	function regionNow(g: DomGuardGroup): Node[] {
		const P = g.parent;
		const mine = new Set<Node>();
		for (const n of g.nodes) if (n.parentNode === P) mine.add(n);
		for (const m of g.members) {
			const d = isText(m) ? display(m, P) : m.parentNode === P ? m : null;
			if (d) mine.add(d);
			if (m.parentNode === P) mine.add(m);
		}
		if (!mine.size) return [];
		const out: Node[] = [];
		let on = false;
		let left = mine.size;
		for (let n = P.firstChild; n && left > 0; n = n.nextSibling) {
			if (mine.has(n)) {
				on = true;
				left--;
			}
			if (on) out.push(n);
		}
		return out;
	}
	function selfShown(g: DomGuardGroup): boolean {
		const P = g.parent;
		for (const n of g.nodes) if (n.parentNode === P) return false;
		let prev: Node | null = null;
		for (const m of g.members) {
			if (m.parentNode !== P) return false;
			if (isText(m) && nvGet(m) !== reactText(m)) return false;
			if (prev !== null && !(prev.compareDocumentPosition(m) & FOLLOWING)) return false;
			prev = m;
		}
		return true;
	}
	function allowRepair(el: Element): boolean {
		const t = now();
		let l = S.lost.get(el);
		if (!l || t - l.t > REPAIR_WINDOW_MS) S.lost.set(el, (l = { n: 0, t }));
		l.n++;
		if (l.n === 2 && el.getAttribute('translate') !== 'no') el.setAttribute('translate', 'no'); // this translator cannot keep it: exclude it
		if (l.n > MAX_REPAIRS) {
			S.stats.repairGiveUp++;
			emit('element-lost-repeatedly', { tag: el.nodeName });
			return false;
		}
		S.stats.repairs++;
		return true;
	}
	function reconcile(g: DomGuardGroup): void {
		if (g.dead) return;
		flush();
		if (g.dead) return;
		const P = g.parent;
		if (!P.isConnected) {
			dissolve(g);
			return;
		}
		if (g.members.length === 0) {
			// every member left: remove what still shows them, then forget the group
			const region = regionNow(g);
			if (region.length) rebuild(g);
			dissolve(g);
			return;
		}
		if (g.forceClone.size === 0 && selfShown(g)) {
			dissolve(g); // e.g. the translator's own "Show original"
			return;
		}
		let repair = false;
		for (const m of g.members) if (m.nodeType === ELEMENT && m.parentNode === null) repair = true;
		if (repair && !g.dirty && g.changed.size === 0) {
			for (const m of g.members) if (isElement(m) && m.parentNode === null && !allowRepair(m)) repair = false;
		}
		if (!g.dirty && g.changed.size === 0 && g.forceClone.size === 0 && !repair) return; // React changed nothing: leave the translation alone
		rebuild(g);
	}
	function removable(n: Node, g: DomGuardGroup, wantSet: Set<Node>): boolean {
		if (wantSet.has(n)) return false;
		if (reactOwned(n)) return S.memberOf.get(n) === g; // a React node: only our own member now shown by a clone
		if (g.nodes.has(n)) return true; // translator output of this run / a replaced display
		const o = S.origin.get(n);
		if (o !== undefined) return S.memberOf.get(o) === g || S.memberOf.get(o) === undefined; // our stale clone
		return false; // anything else (extension, partner, SDK node): never ours to remove
	}
	function rebuild(g: DomGuardGroup): void {
		const P = g.parent;
		const texts = g.members.filter(isText);
		// 1:1 decomposable = every text React did not change has its own display (no merged run)
		const decomposable = texts.every((m) => isBlank(m) || g.changed.has(m) || display(m, P) !== null);
		const want: Node[] = [];
		const made: Node[] = [];
		const replaced = new Set<Node>(); // displays of members that get a new display now
		for (const m of g.members) {
			if (!isText(m)) {
				want.push(m); // an element: always the original (its handlers, focus and refs survive)
				continue;
			}
			const d = display(m, P);
			if (g.forceClone.has(m)) {
				// React re-placed a displaced text itself: a translator never translates it again, so show a clone
			} else if (g.changed.has(m) && m.parentNode === P && nvGet(m) === reactText(m)) {
				want.push(m); // React's in-place write is on screen: keep React's own node
				continue;
			} else if (decomposable && d !== null && !g.changed.has(m)) {
				want.push(d); // unchanged: keep its display (and its translation)
				continue;
			}
			if (d !== null) replaced.add(d);
			const c = doc.createTextNode(reactText(m));
			S.origin.set(c, m);
			S.cloneOf.set(m, c);
			made.push(c);
			want.push(c);
		}
		for (const d of replaced) if (!reactOwned(d)) g.nodes.add(d);
		const wantSet = new Set(want);
		const region = regionNow(g);
		let ref: Node | null;
		if (region.length) ref = region[region.length - 1].nextSibling; // outside the region by construction
		else if (g.prevAnchor && g.prevAnchor.parentNode === P) ref = g.prevAnchor.nextSibling;
		else if (g.nextAnchor && g.nextAnchor.parentNode === P) ref = g.nextAnchor;
		else if (g.prevAnchor === null) ref = P.firstChild; // the run started at P's start
		else {
			ref = null;
			S.stats.lostPosition++;
			emit('lost-position');
		}
		selfMutate(() => {
			for (const n of region) if (n.parentNode === P && removable(n, g, wantSet)) B.removeChild.call(P, n);
			if (ref && ref.parentNode !== P) ref = null;
			let cursor = ref;
			for (let i = want.length - 1; i >= 0; i--) {
				const x = want[i];
				if (!(x.parentNode === P && x.nextSibling === cursor)) B.insertBefore.call(P, x, cursor); // minimal moves: focus, iframes survive
				cursor = x;
			}
		});
		for (const n of g.nodes) if (S.regionOf.get(n) === g) S.regionOf.delete(n);
		const memberSet = new Set(g.members);
		g.nodes = new Set(want.filter((x) => !memberSet.has(x)));
		for (const n of g.nodes) S.regionOf.set(n, g);
		g.changed.clear();
		g.forceClone.clear();
		g.dirty = false;
		S.stats.rebuilds++;
		log({ op: 'rebuild', group: g.id, decomposable, made: made.length });
	}

	// ------------------------------------------------------------ observer records
	function flush(): void {
		const rs = reg.mo.takeRecords();
		if (rs.length) process(rs);
	}
	function selfMutate(fn: () => void): void {
		flush();
		try {
			fn();
		} finally {
			reg.mo.takeRecords(); // our own mutations are not evidence of anything
		}
	}
	function process(records: MutationRecord[]): void {
		if (S.off) {
			S.viaCall.clear();
			return;
		}
		try {
			processInner(records);
		} catch (e) {
			fault('process', e);
		} finally {
			S.viaCall.clear();
		}
	}
	function noteIslandTarget(n: Node | null): void {
		if (!n || !isElement(n)) return;
		if (n.hasAttribute(EXACT_VALUE_ATTR) && reactOwned(n)) {
			S.pendingIslands.add(n);
			schedule();
		}
	}
	function processInner(records: MutationRecord[]): void {
		const byParent = new Map<Node, MutationRecord[]>();
		// characterDataOldValue is always requested (OBSERVE), so a characterData record's oldValue is a string
		const charOld = new Map<Node, string>();
		for (const r of records) {
			if (r.type === 'childList') {
				let list = byParent.get(r.target);
				if (!list) byParent.set(r.target, (list = []));
				list.push(r);
				noteIslandTarget(r.target);
				for (const a of r.addedNodes) {
					if (!isElement(a) || reactOwned(a)) continue;
					if (a.hasAttribute(EXACT_VALUE_ATTR)) S.pendingCopies.add(a);
					else for (const c of a.querySelectorAll('[' + EXACT_VALUE_ATTR + ']')) if (!reactOwned(c)) S.pendingCopies.add(c);
				}
			} else if (r.type === 'characterData') {
				if (!charOld.has(r.target) && r.oldValue !== null) charOld.set(r.target, r.oldValue);
				noteIslandTarget(r.target.parentNode);
			}
		}
		if (S.pendingCopies.size) schedule();
		for (const [P, recs] of byParent) analyzeParent(P, recs, charOld);
		// in-place rewrites of connected React text (Firefox text-only targets)
		for (const [t, old] of charOld) {
			if (!reactOwned(t) || !isCharacterData(t)) continue;
			const parent = t.parentNode;
			if (parent === null) continue;
			const g = S.memberOf.get(t);
			if (g) {
				scheduleGroup(g);
				continue;
			}
			if (!S.reactValue.has(t) && !S.truth.has(t) && nvGet(t) !== old) S.truth.set(t, old);
			if (nvGet(t) !== reactText(t) && S.writtenInPlace.has(t)) {
				// React changed it while the translator was working: its translation may be of the old text
				S.writtenInPlace.delete(t);
				const g2 = newGroup(parent, [t], [], t.previousSibling, t.nextSibling);
				g2.changed.add(t);
				S.stats.reissued++;
				scheduleGroup(g2);
			}
		}
	}
	/** P's children before `recs` (undone newest to oldest), or null on any inconsistency. */
	function invert(P: Node, recs: MutationRecord[]): Node[] | null {
		const list: Node[] = Array.from(P.childNodes);
		for (let i = recs.length - 1; i >= 0; i--) {
			const r = recs[i];
			for (const a of r.addedNodes) {
				const k = list.indexOf(a);
				if (k < 0) return null;
				list.splice(k, 1);
			}
			const prev = r.previousSibling;
			const at = prev ? list.indexOf(prev) + 1 : 0;
			if (prev && at === 0) return null;
			if ((list[at] ?? null) !== r.nextSibling) return null;
			list.splice(at, 0, ...r.removedNodes);
		}
		return list;
	}
	/** A record React's own commit could have produced. */
	function reactLike(r: MutationRecord): boolean {
		for (const n of r.addedNodes) if (!reactOwned(n)) return false;
		for (const n of r.removedNodes) if (!reactOwned(n) || !S.viaCall.has(n)) return false;
		return true;
	}
	const liveRegionNode = (n: Node): boolean => {
		const rg = S.regionOf.get(n);
		return rg !== undefined && !rg.dead;
	};
	function analyzeParent(P: Node, allRecs: MutationRecord[], charOld: Map<Node, string>): void {
		// revalidate groups whose nodes moved
		for (const r of allRecs)
			for (const list of [r.addedNodes, r.removedNodes])
				for (const n of list) {
					const o = S.origin.get(n);
					const g = S.regionOf.get(n) || S.memberOf.get(n) || (o !== undefined ? S.memberOf.get(o) : undefined);
					if (g) scheduleGroup(g);
				}
		// R1: foreign S inserted directly before tracked T, then T removed (not by React) with S right before it
		const pairs: [Node, Node, Node, Node | null][] = [];
		for (const r of allRecs) {
			if (r.addedNodes.length === 1 && r.removedNodes.length === 0) {
				const s = r.addedNodes[0];
				const t = r.nextSibling;
				if (t !== null && tracked(t) && !tracked(s) && !reactOwned(s)) S.insertedBefore.set(s, t);
			}
			for (const t of r.removedNodes) {
				if (!tracked(t) || S.viaCall.has(t)) continue;
				S.slot.set(t, { parent: r.target, next: r.nextSibling });
				const p = r.previousSibling;
				if (r.removedNodes.length === 1 && p && S.insertedBefore.get(p) === t) pairs.push([t, p, r.target, r.nextSibling]);
			}
		}
		const r1 = new Map<Node, Node>();
		for (const [t, s, parent, next] of pairs) {
			S.insertedBefore.delete(s);
			const after = s.nextSibling;
			// or a wrapper containing the old next sibling (Edge re-parenting)
			const slotIntact = after === next || (next != null && after != null && next.parentNode !== parent && after.contains(next));
			if (t.parentNode !== parent && s.parentNode === parent && slotIntact) r1.set(t, s);
		}
		// lone text children (React's single-string path): React never references them;
		// they only need a re-issue when React rewrote them while a translation was in flight
		for (const [t, s] of [...r1]) {
			if (memberLike(t)) continue;
			r1.delete(t);
			if (S.writtenInPlace.has(t)) {
				S.writtenInPlace.delete(t);
				S.pendingLone.set(t, s);
				S.stats.reissued++;
				schedule();
			}
		}
		const k = allRecs.findIndex((r) => !reactLike(r));
		if (k < 0) return;
		const recs = allRecs.slice(k);
		// a translator displaced React's content, or re-translated its own earlier output of a run we track
		let displaced = false;
		for (const r of recs)
			for (const n of r.removedNodes) {
				if (memberLike(n) && !S.viaCall.has(n)) displaced = true;
				if (liveRegionNode(n)) displaced = true;
			}
		if (!displaced) return;
		const before = invert(P, recs);
		if (!before) {
			S.stats.inversionFailed++;
			for (const [t, s] of r1) formFromPairs(P, [[t, s]]);
			return;
		}
		const after: Node[] = Array.from(P.childNodes);
		const touched = new Set<Node>();
		for (const r of recs) {
			for (const n of r.removedNodes) touched.add(n);
			for (const n of r.addedNodes) touched.add(n);
		}
		let i = 0;
		let j = 0;
		let prevAnchor: Node | null = null;
		for (;;) {
			const seg0: Node[] = [];
			const seg1: Node[] = [];
			while (i < before.length && touched.has(before[i])) seg0.push(before[i++]);
			while (j < after.length && touched.has(after[j])) seg1.push(after[j++]);
			const a0 = i < before.length ? before[i] : null;
			const a1 = j < after.length ? after[j] : null;
			if (a0 !== a1) {
				S.stats.inversionFailed++;
				for (const [t, s] of r1) formFromPairs(P, [[t, s]]);
				return;
			}
			if (seg0.length || seg1.length) segment(P, seg0, seg1, prevAnchor, a0, charOld, r1);
			if (a0 === null) break;
			prevAnchor = a0;
			i++;
			j++;
		}
	}
	function formFromPairs(P: Node, pairs: [Node, Node][]): void {
		for (const [t, s] of pairs) {
			const m = S.origin.get(t) ?? t;
			if (!reactOwned(m)) continue;
			S.standIn.set(t, s);
			const old = S.memberOf.get(m);
			if (old !== undefined && !old.dead) {
				old.nodes.add(s);
				S.regionOf.set(s, old);
				markReissue(old, m);
				scheduleGroup(old);
				continue;
			}
			const g = newGroup(P, [m], [s], s.previousSibling, s.nextSibling);
			markReissue(g, m);
		}
	}
	/** Re-issue (§6.7): a text React wrote while on screen may have been captured by a translator before the write. */
	function markReissue(g: DomGuardGroup, m: Node): void {
		const c = S.cloneOf.get(m);
		if (S.writtenInPlace.has(m) || (c && S.writtenInPlace.has(c))) {
			S.writtenInPlace.delete(m);
			if (c) S.writtenInPlace.delete(c);
			g.changed.add(m);
			S.stats.reissued++;
			scheduleGroup(g);
		}
	}
	function segment(P: Node, seg0: Node[], seg1: Node[], prevAnchor: Node | null, nextAnchor: Node | null, charOld: Map<Node, string>, r1: Map<Node, Node>): void {
		if (!seg0.some((n) => (memberLike(n) && !S.viaCall.has(n)) || liveRegionNode(n))) return;
		const seg0Set = new Set(seg0);
		if (seg1.some((n) => reactOwned(n) && !seg0Set.has(n))) {
			// React inserted nodes into this run in the same delivery: do not model it, keep only the 1:1 facts
			S.stats.mixed++;
			const known: [Node, Node][] = [];
			for (const n of seg0) {
				const s = r1.get(n);
				if (s !== undefined) known.push([n, s]);
			}
			formFromPairs(P, known);
			return;
		}
		const members: Node[] = [];
		const merged = new Set<DomGuardGroup>();
		const rep = new Map<Node, Node>(); // member -> the seg0 node that represented it (itself or our clone)
		const add = (m: Node, via: Node | null) => {
			if (!members.includes(m)) members.push(m);
			if (via && !rep.has(m)) rep.set(m, via);
		};
		const expand = (g: DomGuardGroup) => {
			if (merged.has(g) || g.dead) return;
			merged.add(g);
			for (const m of g.members) add(m, null);
		};
		for (const n of seg0) {
			const o = S.origin.get(n);
			if (reactOwned(n)) {
				const g = S.memberOf.get(n);
				if (g) expand(g);
				add(n, n);
			} else if (o !== undefined && reactOwned(o)) {
				// our clone of a React text
				const g = S.memberOf.get(o);
				if (g) expand(g);
				add(o, n);
			} else {
				const rg = S.regionOf.get(n);
				if (rg !== undefined) expand(rg);
			}
			// foreign whitespace / pre-existing foreign nodes: not React's
		}
		const inherited: Node[] = [];
		for (const g of merged) {
			for (const n of g.nodes) if (n.parentNode === P) inherited.push(n); // earlier translator output of the same run
			dissolve(g);
		}
		if (!members.length) return;
		for (const m of members) {
			if (!isText(m)) continue;
			const old = charOld.get(m);
			if (old !== undefined && !S.reactValue.has(m) && !S.truth.has(m)) S.truth.set(m, old);
		}
		// 1:1 displays: R1 pairs first, then a single leftover pair (R2)
		const used = new Set<Node>();
		for (const m of members) {
			const via = rep.get(m) ?? m;
			const s = r1.get(via) ?? r1.get(m);
			if (s !== undefined) {
				S.standIn.set(via, s);
				used.add(s);
			}
		}
		const pairedVia = (m: Node) => r1.get(rep.get(m) ?? m);
		const unpairedMembers = members.filter((m) => isText(m) && !isBlank(m) && pairedVia(m) === undefined && m.parentNode !== P);
		const unpairedNodes = seg1.filter((n) => !reactOwned(n) && !used.has(n) && !isBlank(n) && !memberClone(n));
		if (unpairedMembers.length === 1 && unpairedNodes.length === 1) {
			const m = unpairedMembers[0];
			S.standIn.set(rep.get(m) ?? m, unpairedNodes[0]);
		}
		const g = newGroup(P, members, [...inherited, ...seg1.filter((n) => !reactOwned(n))], prevAnchor, nextAnchor);
		for (const m of members) if (isText(m)) markReissue(g, m);
		// A "restore" of OLD values: the new text is React's current text word for word except for
		// its numbers (e.g. engine2's "Show original" writes back text captured before React changed
		// a price). Not a translation (words identical), so re-showing React's text cannot loop.
		const newText = seg1
			.filter((n): n is Text => isText(n) && !reactOwned(n))
			.map((n) => nvGet(n))
			.join('');
		const nowText = members
			.filter(isText)
			.map((m) => reactText(m))
			.join('');
		if (newText !== nowText && sameWordsOtherNumbers(newText, nowText)) {
			for (const m of members) if (isText(m)) g.changed.add(m);
			S.stats.staleRestores++;
			scheduleGroup(g);
		}
		for (const m of members) if (m.nodeType === ELEMENT && m.parentNode === null) scheduleGroup(g); // lost element
		scheduleGroup(g); // revalidate once (cheap; may dissolve)
		log({ op: 'group', id: g.id, members: members.length, nodes: g.nodes.size });
		checkInstall();
	}

	// ------------------------------------------------------------ deferred work
	function settle(): void {
		S.scheduled = false;
		if (S.off) {
			S.pendingGroups.clear();
			S.pendingWrites.clear();
			S.pendingLone.clear();
			S.pendingIslands.clear();
			S.pendingCopies.clear();
			return;
		}
		try {
			flush();
		} catch (e) {
			fault('settle-flush', e);
		}
		const writes = [...S.pendingWrites];
		S.pendingWrites.clear();
		for (const t of writes) {
			try {
				if (t.parentNode !== null) continue;
				const g = S.memberOf.get(t);
				if (g) {
					g.changed.add(t);
					S.pendingGroups.add(g);
				} else {
					S.stats.unmappedText++;
					emit('unmapped-text');
				}
			} catch (e) {
				fault('settle-write', e);
			}
		}
		const lone = [...S.pendingLone];
		S.pendingLone.clear();
		for (const [t, s] of lone) {
			try {
				const P = s.parentNode;
				if (t.parentNode !== null || P === null || !reactOwned(P) || !isCharacterData(t)) continue;
				selfMutate(() => {
					const c = doc.createTextNode(nvGet(t));
					S.origin.set(c, t);
					B.insertBefore.call(P, c, s);
					B.removeChild.call(P, s);
				});
			} catch (e) {
				fault('settle-lone', e);
			}
		}
		const groups = [...S.pendingGroups];
		S.pendingGroups.clear();
		for (const g of groups) {
			try {
				reconcile(g);
			} catch (e) {
				fault('reconcile', e);
			}
		}
		const copies = [...S.pendingCopies];
		S.pendingCopies.clear();
		for (const c of copies) {
			try {
				const parent = c.parentNode;
				if (parent && !reactOwned(c)) {
					selfMutate(() => B.removeChild.call(parent, c));
					S.stats.islandCopies++;
				}
			} catch (e) {
				fault('settle-copy', e);
			}
		}
		const islands = [...S.pendingIslands];
		S.pendingIslands.clear();
		for (const el of islands) {
			try {
				checkIsland(el);
			} catch (e) {
				fault('settle-island', e);
			}
		}
	}
	function checkIsland(el: Element): void {
		if (!el.isConnected || !reactOwned(el)) return;
		const want = el.getAttribute(EXACT_VALUE_ATTR);
		if (want === null) return; // no value to verify against: never guess one
		const f = el.firstChild;
		if (want === '' ? f === null : f !== null && f === el.lastChild && isText(f) && nvGet(f) === want) return;
		const t = now();
		let r = S.restores.get(el);
		if (!r || t - r.t > REPAIR_WINDOW_MS) S.restores.set(el, (r = { n: 0, t }));
		if (++r.n > MAX_ISLAND_RESTORES) {
			emit('island-rewritten-repeatedly');
			return;
		}
		selfMutate(() => {
			el.textContent = want;
		});
		S.stats.islandRestores++;
	}

	// ------------------------------------------------------------ trampoline decisions
	// A decision made BEFORE the native call only reads the model. For a call that looks accepted, every change to the
	// model waits until the native call returned (C1: bookkeeping only): a call the browser still rejects (an arity
	// error, a HierarchyRequestError, a layer below that throws) must leave the model, and so the screen, as it was.
	function planRemove(P: unknown, child: unknown): Plan {
		if (!isNode(child)) return PASS;
		if (child.parentNode === P) {
			// ACCEPTED call: NO flush here (a page-world translator's insert+remove must stay one delivery)
			if (reactOwned(child)) {
				const prev = child.previousSibling;
				const pw = prev !== null && S.callInsBefore.get(prev) === child; // a page-world translator displacing it
				if (!pw) return TAG; // React's own removal: never evidence of a translator (noteRemoved)
			}
			return PASS;
		}
		if (!reactOwned(child)) return PASS; // not ours: the native error, exactly as without the guard
		if (!isNode(P)) return PASS; // not a node receiver: the native TypeError
		flush();
		const g = S.memberOf.get(child);
		if (g && g.parent === P) {
			detachMember(g, child);
			rebuild(g);
			if (g.members.length === 0) dissolve(g);
			S.stats.healRemove++;
			log({ op: 'heal removeChild', group: g.id });
			return DONE;
		}
		S.stats.unmappedRemove++;
		emit('unmapped-remove', { tag: P.nodeName });
		if (child.parentNode) B.removeChild.call(child.parentNode, child); // moved elsewhere by a third party: honour the removal
		return DONE;
	}
	/** After React's own removal of `child` returned: its run no longer shows it, and the delivery must not model it. */
	function noteRemoved(child: Node): void {
		tagViaCall(child); // after the native call: the observer delivery was queued first
		const g = S.memberOf.get(child);
		if (g) {
			detachMember(g, child);
			scheduleGroup(g);
		}
	}
	function fallbackAnchor(ref: Node, P: Node): Node | null {
		let n: Node | null = ref;
		for (let i = 0; i < 16 && n; i++) {
			const s = S.slot.get(n);
			if (!s || s.parent !== P) return null;
			n = s.next;
			if (!n) return null;
			if (n.parentNode === P) return n;
			const d = isText(n) ? display(n, P) : null;
			if (d) return d;
		}
		return null;
	}
	/** Read BEFORE an accepted insert moves `node`: a detached text of a group that some display still shows. */
	function displacedText(node: Node): boolean {
		return isText(node) && node.parentNode === null && S.memberOf.has(node) && displayAnywhere(node) !== null;
	}
	/**
	 * Bookkeeping for an ACCEPTED insert, run once the native call returned. `wasDisplaced` is displacedText(node)
	 * from before the call. true => React re-placed a displaced text itself: show a clone there (reclone).
	 */
	function notePlacement(P: Node, node: Node, ref: Node | null, wasDisplaced: boolean): boolean {
		if (ref !== null && !reactOwned(node) && reactOwned(ref)) noteCallInsert(node, ref); // maybe a page-world translator
		if (!S.active) return false;
		const og = S.memberOf.get(node);
		if (og !== undefined) {
			// React never inserts before a foreign node, or into a translator's node
			const agent = (ref !== null && !reactOwned(ref)) || (!reactOwned(P) && S.regionOf.has(P));
			if (agent) scheduleGroup(og); // a translator putting React's node back ("Show original"): revalidate only
			else {
				detachMember(og, node);
				scheduleGroup(og);
				if (wasDisplaced) return true; // React re-placed it itself: show a clone there, so it translates again
			}
		}
		if (ref !== null) {
			const rg = S.memberOf.get(ref);
			if (rg !== undefined && rg.parent === P) {
				insertMember(rg, node, ref); // ref reused by Firefox/Safari
				scheduleGroup(rg);
			}
		}
		return false;
	}
	function planInsert(P: unknown, node: unknown, ref: unknown): Plan {
		// ACCEPTED-looking call (a missing second argument lands here too, and fails natively): bookkeeping after it,
		// and only when there is any to do (every DOM call on the partner's page passes through here)
		if (ref == null || (isNode(ref) && ref.parentNode === P)) {
			if (!isNode(node) || !isNode(P)) return PASS;
			return S.active || (ref != null && !reactOwned(node) && reactOwned(ref)) ? NOTE : PASS;
		}
		if (!reactOwned(ref) || !isNode(ref)) return PASS;
		if (!isNode(P) || !isNode(node)) return PASS; // not nodes: the native TypeError
		flush();
		const g = S.memberOf.get(ref);
		if (g && g.parent === P) {
			insertMember(g, node, ref);
			rebuild(g); // SYNC: layout effects measure it now
			S.stats.healInsert++;
			log({ op: 'heal insertBefore', group: g.id });
			return DONE;
		}
		S.stats.unmappedInsert++;
		emit('unmapped-insert', { tag: P.nodeName });
		B.insertBefore.call(P, node, fallbackAnchor(ref, P)); // never drop React's node
		return DONE;
	}
	function reclone(P: Node, t: Node): void {
		// React re-placed a displaced text itself (keyed move): show a fresh clone there so it translates again
		if (t.parentNode !== P) return;
		const g = newGroup(P, [t], [], t.previousSibling, t.nextSibling);
		g.forceClone.add(t);
		scheduleGroup(g);
	}
	function safeRemove(P: unknown, child: unknown, args: IArguments): unknown {
		// breaker tripped: no healing, but our displaced nodes still never throw
		try {
			if (isNode(child) && child.parentNode !== P && reactOwned(child)) return child;
		} catch {
			/* fall through to native */
		}
		const r: unknown = Reflect.apply(B.removeChild, P, args);
		return r;
	}
	function safeInsert(P: unknown, node: unknown, ref: unknown, args: IArguments): unknown {
		try {
			if (isNode(ref) && ref.parentNode !== P && reactOwned(ref) && isNode(P) && isNode(node)) return B.appendChild.call(P, node);
		} catch {
			/* fall through to native */
		}
		const r: unknown = Reflect.apply(B.insertBefore, P, args);
		return r;
	}

	function checkInstall(): void {
		const T = reg.trampolines;
		const notOnTop: string[] = [];
		if (NP.removeChild !== T.removeChild) notOnTop.push('removeChild');
		if (NP.insertBefore !== T.insertBefore) notOnTop.push('insertBefore');
		if (NP.appendChild !== T.appendChild) notOnTop.push('appendChild');
		const nv = Object.getOwnPropertyDescriptor(NP, 'nodeValue');
		if (!nv || nv.set !== T.nodeValueSet) notOnTop.push('nodeValue');
		reg.diag.notOnTop = notOnTop;
		if (notOnTop.length) emit('not-on-top', { which: notOnTop.join(',') });
	}

	return {
		removeChild(P, child, args) {
			if (S.off) return safeRemove(P, child, args);
			let plan = PASS;
			try {
				plan = planRemove(P, child);
			} catch (e) {
				fault('removeChild', e);
				try {
					if (isNode(child) && child.parentNode !== P && reactOwned(child)) {
						if (child.parentNode) B.removeChild.call(child.parentNode, child);
						return child;
					}
				} catch {
					/* native below */
				}
			}
			if (plan === DONE) return child;
			const r: unknown = Reflect.apply(B.removeChild, P, args);
			try {
				if (plan === TAG && isNode(child)) noteRemoved(child);
			} catch (e) {
				fault('removeChild-after', e);
			}
			return r;
		},
		insertBefore(P, node, ref, args) {
			if (S.off) return safeInsert(P, node, ref, args);
			let plan = PASS;
			let moved = false;
			let wasDisplaced = false;
			try {
				if (isNode(node)) {
					moved = node.parentNode !== null && reactOwned(node);
					if (isNode(P)) ensureCovered(P, node); // BEFORE the native insert: the observer must see it
				}
				plan = planInsert(P, node, ref);
				if (plan === NOTE && S.active && isNode(node)) wasDisplaced = displacedText(node);
			} catch (e) {
				fault('insertBefore', e);
				try {
					if (isNode(ref) && ref.parentNode !== P && reactOwned(ref) && isNode(P) && isNode(node)) return B.appendChild.call(P, node);
				} catch {
					/* native below */
				}
			}
			if (plan === DONE) return node;
			const r: unknown = Reflect.apply(B.insertBefore, P, args);
			if (!moved && plan !== NOTE) return r;
			try {
				if (isNode(node)) {
					if (moved) tagViaCall(node);
					if (plan === NOTE && isNode(P) && notePlacement(P, node, isNode(ref) ? ref : null, wasDisplaced)) reclone(P, node);
				}
			} catch (e) {
				fault('insertBefore-after', e);
			}
			return r;
		},
		appendChild(P, node, args) {
			if (S.off) {
				const r: unknown = Reflect.apply(B.appendChild, P, args);
				return r;
			}
			let plan = PASS;
			let moved = false;
			let wasDisplaced = false;
			try {
				if (isNode(node)) {
					moved = node.parentNode !== null && reactOwned(node);
					if (isNode(P)) {
						ensureCovered(P, node); // BEFORE the native insert: the observer must see it
						if (S.active) {
							plan = NOTE; // appendChild has no ref: nothing to note before a translator was seen
							wasDisplaced = displacedText(node);
						}
					}
				}
			} catch (e) {
				fault('appendChild', e);
			}
			const r: unknown = Reflect.apply(B.appendChild, P, args);
			if (!moved && plan !== NOTE) return r;
			try {
				if (isNode(node)) {
					if (moved) tagViaCall(node);
					if (plan === NOTE && isNode(P) && notePlacement(P, node, null, wasDisplaced)) reclone(P, node);
				}
			} catch (e) {
				fault('appendChild-after', e);
			}
			return r;
		},
		setNodeValue(node, _value, args) {
			Reflect.apply(B.nodeValueSet, node, args); // the write always lands first
			if (S.off || S.keys.length === 0) return;
			try {
				if (!isNode(node) || !isText(node)) return;
				const P = node.parentNode;
				if (reactOwned(node)) {
					S.reactValue.set(node, nvGet(node));
					S.truth.delete(node);
					const g = S.memberOf.get(node);
					if (g) {
						g.changed.add(node);
						scheduleGroup(g);
					} else if (P !== null) S.writtenInPlace.add(node);
					else {
						S.pendingWrites.add(node); // displaced in this very task? resolved after the next flush
						schedule();
					}
				} else if (P !== null && reactOwned(P)) {
					S.writtenInPlace.add(node); // React's single-string path wrote a lone text child
					const eg = S.memberOf.get(P);
					if (eg) {
						eg.dirty = true; // the element's paragraph may hold stale copies of it
						scheduleGroup(eg);
					}
				}
			} catch (e) {
				fault('nodeValue', e);
			}
		},
		process,
		checkInstall,
		registerReactRoot(el) {
			try {
				const found = registerKeysOf(el, false);
				if (!S.covered.has(el)) observe(el);
				checkInstall();
				if (!found) emit('no-react-key');
				return found;
			} catch (e) {
				fault('registerReactRoot', e);
				return false;
			}
		},
		registerReactSubtree(rootContainer, subtree) {
			try {
				// The KEY is the root's (`hydrateRoot` marked the container, never the subtree); the observation is the
				// subtree's alone (the root may be the whole document, <head> included, G7).
				const found = registerKeysOf(rootContainer, true);
				if (!S.covered.has(subtree)) observe(subtree);
				checkInstall();
				if (!found) emit('no-react-key');
				return found;
			} catch (e) {
				fault('registerReactSubtree', e);
				return false;
			}
		},
		onEvent(cb) {
			S.listeners.push(cb);
		},
		stats: () => ({ ...S.stats, off: S.off, active: S.active, keys: S.keys.length, faultsList: S.lastFaults, diag: reg.diag, implVersion: reg.implVersion }),
	};
}
